import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  AcceptedQuoteDepositRootScreenParamList,
  CheckoutRootParamList,
} from "../../../../types";
import { usePaymentControllerProvider } from "../../utils/payment-controller";
import { moneyToFormattedString } from "@towersystems/roam-common/lib/shared-utils";
import {
  Page,
  View,
  TextField,
  Text,
  Stack,
  Divider,
  Button,
} from "@towersystems/roam-common-native";
import { useTranslation } from "../../../../utilities/i18n";
import { Spacings } from "react-native-ui-lib";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { compareLessThanWithTolerance } from "../../../../utilities/compare-with-tolerance";
import { useCheckoutContextProvider } from "../../utils/checkout";
import { SelectablePaymentMethodButtonList } from "../../components/SelectablePaymentMethodButtonList";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { onlineOnlyIds } from "../SelectPaymentMethodScreen";
import React from "react";
import PaymentMethod from "../../../../utilities/data/model/PaymentMethod";
import { useFocusEffect } from "@react-navigation/native";
import { useCreateSaleQuoteRootScreenContextProvider } from "../CreateSaleQuoteRootScreen/hooks";
import { SaleState } from "../../../../utilities/data/model/Sale";

export type AcceptedQuoteDepositScreenProps = NativeStackScreenProps<
  AcceptedQuoteDepositRootScreenParamList & CheckoutRootParamList,
  "AcceptedQuoteDepositScreen"
>;

const minDepositAmount = 0;

export const AcceptedQuoteDepositScreen = ({
  navigation,
}: AcceptedQuoteDepositScreenProps) => {
  const online = useOnline();

  const { t } = useTranslation("quote");
  const { sale } = useCreateSaleQuoteRootScreenContextProvider();

  const { tenderedAmountInput, validTenderedAmount, setTenderedAmountInput } =
    usePaymentControllerProvider();

  const { selectablePaymentMethods, balance } = useCheckoutContextProvider();

  const shouldFocus = useHasTransitionEnded(navigation);

  useFocusEffect(
    React.useCallback(() => {
      setTenderedAmountInput("0");
    }, [])
  );

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          label={t("button.skip_payment")}
          isAction
          onPress={async () => {
            await sale.transitionToState(SaleState.QUOTE_OPEN);
            navigation.reset({
              index: 0,
              routes: [{ name: "CheckoutSummaryRootScreen" }],
            });
          }}
          size="compact"
        ></Button>
      ),
    });
  }, []);

  const handleOnPressAll = React.useCallback((item: PaymentMethod) => {
    navigation.navigate("PaymentRootScreen", {
      screen: "PaymentScreen",
      paymentMethodId: item.id,
      tenderedAmount: Number(tenderedAmountInput),
      minTenderAmount: Number(tenderedAmountInput),
    });
  }, []);

  const normalizedValidTenderedAmount =
    validTenderedAmount &&
    compareLessThanWithTolerance(
      minDepositAmount,
      Number(tenderedAmountInput)
    ) &&
    compareLessThanWithTolerance(Number(tenderedAmountInput), balance);

  const overTendered =
    validTenderedAmount && Number(tenderedAmountInput) > balance;

  return (
    <Page
      titleContainerStyle={{ alignItems: "center" }}
      title={t("accepted_quote_deposit")}
    >
      <Stack vertical>
        <View>
          <Text
            style={{
              textTransform: "uppercase",
              fontWeight: "bold",
              marginBottom: Spacings.s4,
            }}
          >
            {t("pos:enter_deposit")}
          </Text>
          <View>
            <TextField
              onChangeText={setTenderedAmountInput}
              value={tenderedAmountInput}
              selectTextOnFocus
              autoFocus={shouldFocus}
              formatterType={
                normalizedValidTenderedAmount ? "money" : undefined
              }
              validationMessage={
                !normalizedValidTenderedAmount
                  ? `Enter number between ${moneyToFormattedString(
                      minDepositAmount.toString()
                    )} and ${moneyToFormattedString(balance)}`
                  : ""
              }
            />
          </View>
        </View>
        <Divider />
        <SelectablePaymentMethodButtonList
          wrapped
          items={selectablePaymentMethods}
          allDisabled={!normalizedValidTenderedAmount || overTendered}
          onPressAll={handleOnPressAll}
          disabledFn={(item) =>
            normalizedValidTenderedAmount &&
            ((balance == 0 && item.payType.id !== "1") ||
              (Number(tenderedAmountInput) == 0 && item.payType.id !== "1") ||
              (onlineOnlyIds.includes(item.payType.id) && !online))
          }
        />
      </Stack>
    </Page>
  );
};
